// src/pages/analytics/InvoicesTab.tsx
//
// Invoices & Penetration — category × period matrix (recipe:
// sales-category-matrix, agent 3.20.0+, feature analytics.invoices). Ports
// two PBI pages into one tab:
//   • "Invoices": invoice count, average ticket ($ / invoice), % of the
//     period's dollars;
//   • "Customers": how many customers bought each category, penetration
//     (% of that period's buying customers) and $ per customer.
// The Show pills pick which number fills the cells; one request returns them
// all. Distinct counts come from the server's GROUPING SETS, so totals are
// real distinct counts, not sums of cells. Gregorian or Hebrew months (the
// tenant's analytics.default_calendar is the default). The filter pane
// narrows everything.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuthContext } from '../../hooks/useAuth';
import { adminApi } from '../../services/adminApi';
import { apiV2 as api } from '../../services/apiV2';
import type { CategoryMatrixCell, CategoryMatrixRow, CategoryMatrixResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

type Metric = keyof CategoryMatrixCell;
const METRICS: { value: Metric; label: string; fmt: (v: number) => string; group: 'Invoices' | 'Customers' | 'Dollars' }[] = [
  { value: 'invoices',       label: 'Invoices',       fmt: fmtNum, group: 'Invoices' },
  { value: 'avgInvoice',     label: 'Avg / Invoice',  fmt: fmtCur, group: 'Invoices' },
  { value: 'customers',      label: 'Customers',      fmt: fmtNum, group: 'Customers' },
  { value: 'penetrationPct', label: '% of Customers', fmt: fmtPct, group: 'Customers' },
  { value: 'avgCustomer',    label: '$ / Customer',   fmt: fmtCur, group: 'Customers' },
  { value: 'sales',          label: 'Sales $',        fmt: fmtCur, group: 'Dollars' },
  { value: 'pctOfSales',     label: '% of $',         fmt: fmtPct, group: 'Dollars' },
];

const ROW_DIMS = [
  { value: 'itemCategory',     label: 'Item Category' },
  { value: 'itemSubcategory',  label: 'Item Subcategory' },
  { value: 'customerCategory', label: 'Customer Category' },
  { value: 'salesman',         label: 'Salesman' },
  { value: 'area',             label: 'Area' },
];
const BUCKETS = [
  { value: 'month',   label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'week',    label: 'Week' },
];
const CALENDARS = [
  { value: 'gregorian', label: 'Gregorian' },
  { value: 'hebrew',    label: 'Hebrew' },
];
const YEARS = [
  { value: 0, label: 'This year' },
  { value: 1, label: 'Last year' },
  { value: 2, label: '2 years ago' },
];

const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

function exportMatrix(data: CategoryMatrixResponse, metric: Metric, rowLabel: string, exporter: typeof exportCsv = exportCsv) {
  const label = METRICS.find(m => m.value === metric)?.label ?? metric;
  const cols: CsvColumns<CategoryMatrixRow> = [
    [rowLabel, (r) => r.label],
    ...data.periods.map((p, i): [string, (r: CategoryMatrixRow) => string | number] => [p.label, (r) => r.cells[i]?.[metric] ?? '']),
    [`Total (${label})`, (r) => r.total[metric]],
  ];
  exporter(`sales-${metric}-${data.year}`, cols, [...data.rows, data.totalRow]);
}

export function InvoicesTab() {
  const { claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;
  const [metric, setMetric] = useState<Metric>('invoices');
  const [rowDim, setRowDim] = useState('itemCategory');
  const [bucket, setBucket] = useState('month');
  const [yearOffset, setYearOffset] = useState(0);
  const [calendarOverride, setCalendarOverride] = useState<string | null>(null);
  const pane = useFilterPane();

  const resetFilters = () => {
    setMetric('invoices'); setRowDim('itemCategory'); setBucket('month'); setYearOffset(0); setCalendarOverride(null);
  };

  // Same tenant default-calendar lookup (and cache key) as the YoY tab.
  const calendarDefaultQ = useQuery({
    queryKey: ['analyticsYoy', 'default-calendar', slug],
    queryFn: () => adminApi.getConfig('analytics.default_calendar', slug ?? undefined),
    enabled: !!slug,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const calendar = calendarOverride ?? (calendarDefaultQ.data?.value === 'hebrew' ? 'hebrew' : 'gregorian');

  const q = useQuery({
    queryKey: ['analyticsCategoryMatrix', rowDim, bucket, calendar, yearOffset, pane.key],
    queryFn: () => api.getCategoryMatrix({ rowDim, bucket, calendar, yearOffset }, pane.params),
    enabled: !slug || calendarOverride !== null || !calendarDefaultQ.isLoading,
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const periods = data?.periods ?? [];
  const rows = data?.rows ?? [];
  const m = METRICS.find(x => x.value === metric) ?? METRICS[0];
  const rowLabel = ROW_DIMS.find(d => d.value === rowDim)?.label ?? rowDim;

  // Heat shading on the shown metric (row cells only) so strong/weak cells pop.
  const max = Math.max(0, ...rows.flatMap(r => r.cells.map(c => c?.[metric] ?? 0)));
  const shade = (v: number) => max <= 0 ? undefined : { backgroundColor: `rgba(59,130,246,${(0.04 + 0.28 * (v / max)).toFixed(3)})` };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Receipt size={20} /> Invoices &amp; Penetration
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Invoice count, average ticket and customer penetration by {rowLabel.toLowerCase()} and {bucket}
          {data && <span className="text-slate-500"> · {data.calendar} year {data.year} ({data.dateFrom} → {data.dateTo})</span>}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Show</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {METRICS.map((x, i) => (
              <span key={x.value} className="contents">
                {i > 0 && METRICS[i - 1].group !== x.group && <span className="w-px h-5 bg-slate-700/60 mx-0.5" />}
                <button onClick={() => setMetric(x.value)} className={pill(metric === x.value)}>{x.label}</button>
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Rows</span>
          <select value={rowDim} onChange={e => setRowDim(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {ROW_DIMS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Bucket</span>
          <div className="flex items-center gap-1.5">
            {BUCKETS.map(b => <button key={b.value} onClick={() => setBucket(b.value)} className={pill(bucket === b.value)}>{b.label}</button>)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Calendar</span>
          <div className="flex items-center gap-1.5">
            {CALENDARS.map(c => <button key={c.value} onClick={() => setCalendarOverride(c.value)} className={pill(calendar === c.value)}>{c.label}</button>)}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Year</span>
          <select value={yearOffset} onChange={e => setYearOffset(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {YEARS.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="invoices"
          getState={() => ({ metric, rowDim, bucket, yearOffset, calendar: calendarOverride })}
          applyState={(s) => {
            if (typeof s.metric === 'string' && METRICS.some(m => m.value === s.metric)) setMetric(s.metric as Metric);
            if (typeof s.rowDim === 'string' && ROW_DIMS.some(d => d.value === s.rowDim)) setRowDim(s.rowDim);
            if (typeof s.bucket === 'string' && BUCKETS.some(b => b.value === s.bucket)) setBucket(s.bucket);
            if (typeof s.yearOffset === 'number' && YEARS.some(y => y.value === s.yearOffset)) setYearOffset(s.yearOffset);
            setCalendarOverride(s.calendar === 'hebrew' || s.calendar === 'gregorian' ? s.calendar : null);
          }}
        />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button onClick={() => data && exportMatrix(data, metric, rowLabel)} disabled={!data || rows.length === 0}
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button onClick={() => data && exportMatrix(data, metric, rowLabel, exportXlsx)} disabled={!data || rows.length === 0}
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <FilterPane />

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the matrix</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading || (!q.data && !q.error) ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">{rowLabel}</th>
                  {periods.map(p => <th key={p.key} className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{p.label}</th>)}
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium border-l border-slate-700/40">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.label}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={r.label}>{r.label}</td>
                    {r.cells.map((c, ci) => (
                      <td key={ci} className="px-3 py-2 text-right tabular-nums text-slate-200" style={c ? shade(c[metric]) : undefined}
                        title={c ? `${fmtCur(c.sales)} · ${fmtNum(c.invoices)} invoices · ${fmtNum(c.customers)} customers` : undefined}>
                        {c ? m.fmt(c[metric]) : <span className="text-slate-600">–</span>}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums text-white border-l border-slate-800/60">{m.fmt(r.total[metric])}</td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                    <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                    {data.totalRow.cells.map((c, ci) => (
                      <td key={ci} className="px-3 py-2 text-right tabular-nums text-white">{c ? m.fmt(c[metric]) : '–'}</td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums text-white border-l border-slate-700/60">{m.fmt(data.totalRow.total[metric])}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
            Totals are distinct counts (an invoice with two categories counts once in the total).
            {metric === 'penetrationPct' && ' Penetration = customers buying the row ÷ all customers who bought anything that period.'}
          </div>
        </div>
      )}
    </div>
  );
}
